/**
 * ui-tokens.ts
 *
 * Single source of truth for all semantic color / style maps in the Quire
 * dashboard. Every value references a CSS custom property from index.css —
 * change a variable there and every page updates automatically.
 *
 * Usage:
 *   import { REQUEST_STATUS, PILL } from "@/lib/ui-tokens"
 *   <span className={PILL[status]}>{STATUS_LABEL[status]}</span>
 */

// ── Request / document status ─────────────────────────────────────────────────

export type RequestStatus = "draft" | "pending" | "partial" | "received" | "approved" | "overdue"

export const REQUEST_STATUS_LABEL: Record<RequestStatus, string> = {
  draft:    "Draft",
  pending:  "Pending",
  partial:  "Partial",
  received: "Received",
  approved: "Approved",
  overdue:  "Overdue",
}

/** Pill background + text + border — all via CSS vars */
export const REQUEST_PILL: Record<RequestStatus, string> = {
  draft:    "bg-(--muted-token) text-muted-foreground border border-(--border)",
  pending:  "bg-(--accent-bg)  text-(--accent)  border border-(--colors-amber-200)",
  partial:  "bg-(--colors-blue-100) text-(--trust-blue) border border-(--colors-blue-200)",
  received: "bg-(--green-bg)   text-(--green)   border border-(--colors-green-200)",
  approved: "bg-(--green-bg)   text-(--green)   border border-(--colors-green-200)",
  overdue:  "bg-(--colors-red-100) text-(--warn) border border-(--colors-red-200)",
}

/** Progress bar fill color */
export const REQUEST_PROGRESS_COLOR: Record<RequestStatus, string> = {
  draft:    "var(--border)",
  pending:  "var(--colors-amber-300)",
  partial:  "var(--colors-blue-300)",
  received: "var(--colors-green-200)",
  approved: "var(--colors-green-200)",
  overdue:  "var(--colors-red-300)",
}

// ── Signature status ──────────────────────────────────────────────────────────

export type SignatureStatus = "pending" | "signed" | "declined"

export const SIGNATURE_PILL: Record<SignatureStatus, string> = {
  pending:  "bg-(--accent-bg) text-(--accent) border border-(--colors-amber-200)",
  signed:   "bg-(--green-bg) text-(--green) border border-(--colors-green-200)",
  declined: "bg-(--colors-red-100) text-(--warn) border border-(--colors-red-200)",
}

export const SIGNATURE_STATUS_LABEL: Record<SignatureStatus, string> = {
  pending:  "Pending",
  signed:   "Signed",
  declined: "Declined",
}

// ── Workspace member role ─────────────────────────────────────────────────────

export type MemberRole = "owner" | "admin" | "member"

export const ROLE_PILL: Record<MemberRole, string> = {
  owner:  "bg-(--accent-bg) text-(--accent) border border-(--colors-amber-200)",
  admin:  "bg-(--colors-blue-100) text-(--trust-blue) border border-(--colors-blue-200)",
  member: "bg-(--muted-token) text-muted-foreground border border-(--border)",
}

export const ROLE_LABEL: Record<MemberRole, string> = {
  owner:  "Owner",
  admin:  "Admin",
  member: "Member",
}

// ── Feedback type ─────────────────────────────────────────────────────────────

export type FeedbackType = "bug" | "improvement" | "feature" | "other"

export const FEEDBACK_PILL: Record<FeedbackType, string> = {
  bug:         "bg-(--colors-red-100) text-(--warn) border border-(--colors-red-200)",
  improvement: "bg-(--accent-bg) text-(--accent) border border-(--colors-amber-200)",
  feature:     "bg-(--colors-blue-100) text-(--trust-blue) border border-(--colors-blue-200)",
  other:       "bg-(--muted-token) text-muted-foreground border border-(--border)",
}

export const FEEDBACK_LABEL: Record<FeedbackType, string> = {
  bug:         "Bug",
  improvement: "Improvement",
  feature:     "Feature",
  other:       "Other",
}

// ── Shared layout primitives referenced as CSS vars ───────────────────────────

/**
 * Table header row — references CSS vars so theme + radius changes propagate.
 * Use: <div className={TABLE_HEADER_ROW}>
 */
export const TABLE_HEADER_ROW =
  "flex items-center justify-between px-4 py-2.5 border-b border-border bg-(--colors-stone-50) dark:bg-(--colors-stone-100)"

export const TABLE_COL_HEADER =
  "text-[9.5px] font-bold uppercase tracking-[0.08em] text-muted-foreground"

export const CARD_SECTION =
  "border border-border bg-card overflow-hidden"

export const PAGE_HEADER_EYEBROW =
  "text-[10px] font-bold uppercase tracking-widest text-(--brand) mb-0.5"

export const PAGE_SHELL =
    "mx-auto w-full max-w-[1154px] px-4 sm:px-5 py-5 space-y-5"

export const ACTION_BTN =
  "flex items-center gap-1.5 bg-foreground text-background text-[11px] font-bold px-3 h-8 hover:opacity-90 transition-opacity"

export const GHOST_BTN =
  "flex items-center gap-1.5 border border-border bg-card text-[11px] font-medium text-muted-foreground px-3 h-8 hover:text-foreground hover:bg-muted/50 transition-colors"
